import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { Ticket } from './ticket';
import { Product } from './product';
import { User } from './user';
// `import type` to break the cycle with productselect.ts (which already
// imports TicketProduct). ProductSelect is only used here in type
// positions, so this is erased at compile time.
import type { ProductSelect } from './productselect';
import { TicketOption } from './ticketoption';
import { ProductOption } from './productoption';
import { TicketOffer } from './ticketoffer';

export interface _TicketProduct {
    status: string,
    product: number,
    waiter: number,     // USER objid del camarero que añadió esta línea
    offer: number,
    price: number,
    charge: number,
    fixed: boolean,     // true if it has been manually repriced
    market: number,
    weight: number,
    taxrate: number,
    ticket: number,
    flags: string,
    comments: string,
    sort: number,
    group?: number      // calculated based on sort and separators
};

interface _TicketProductData extends _TicketProduct {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class TicketProductData extends DataObject {
    protected _product: _TicketProductData = {
        status: null,
        product: null,
        waiter: null,
        offer: null,
        price: null,
        charge: null,
        fixed: false,
        market: null,
        weight: null,
        taxrate: null,
        ticket: null,
        flags: null,
        comments: null,
        sort: null,
        group: null,
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._product.created = new Date();
    }

    /****************************/
    /* TICKET PRODUCT FLAGS     */
    /****************************/

    protected TicketProductStatus = Object.freeze({
        IsAC: 0,    // has 'AC' status
        IsUN: 1,    // has 'UN' status
        IsRD: 2,    // has 'RD' status
        IsPP: 3,    // has 'PP' status
        IsPD: 4,    // has 'PD' status
        IsCC: 5,    // has 'CC' status
        IsDE: 6     // has 'DE' status
    });

    protected get flags(): Array<any> {
        let _array = [ false, false, false, false, false, false, false ];

        if (this._product.flags){
            let _flags = Array.from(this._product.flags);
            for(let i=0; i < _flags.length; i++){
                _array[i] = (_flags[i] == '1');
            }        
        }

        return _array;
    }

    protected set flags(value: Array<any>) {
        let _array = [];
        for(let i=0; i < value.length; i++){
            _array[i] = (value[i] === true) ? '1' : '0';
        }

        let _flags = _array.join(''); 
        if(this.patchValue(this._product, 'flags', _flags)){
            this.ToUpdate = true;
        }
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(): Date {
        return this._product.created;
    }

    get updated(): Date {
        return this._product.updated || this._product.created;;
    }

    get status(): string{
        return this._product.status;
    }

    set status(value: string){
        if (this.patchValue(this._product, 'status', value)){
            this.ToUpdate = true;
        }
    }

    get product(): Product {
        return this._children['product'] || null;
    }

    set product(value: Product){
        if (this.SetChild('product', value, 'product')){
            this.ToUpdate = true;
        }
    }

    // Camarero (USER) que añadió esta línea. Referencia a USER (como product/
    // offer): va en Depend para resolver el FK, NO en Children (no se commitea
    // el usuario).
    get waiter(): User {
        return this._children['waiter'] || null;
    }

    set waiter(value: User){
        if (this.SetChild('waiter', value, 'waiter')){
            // mantener el objid de backing: el waiter se envía como CAMPO (no por
            // Depend) para no notificar la fila USER del camarero (fuga de PII).
            this.patchValue(this._product, 'waiter', value ? parseInt(value.objid) : null);
            this.ToUpdate = true;
        }
    }

    get offer(): TicketOffer {
        return this._children['offer'] || null;
    }

    set offer(value: TicketOffer){
        if (this.SetChild('offer', value, 'offer')){
            this.ToUpdate = true;
        }
    }

    get price(): number {
        return this._product.price;
    }

    set price(value: number){
        if (this.patchValue(this._product, 'price', value)){
            this.ToUpdate = true;
        }
    }

    get charge(): number {
        return this._product.charge;
    }

    set charge(value: number){
        if (this.patchValue(this._product, 'charge', value)){
            this.ToUpdate = true;
        }
    }

    get fixed(): boolean {
        return this._product.fixed;
    }

    set fixed(value: boolean){
        if (this.patchValue(this._product, 'fixed', value)){
            this.ToUpdate = true;
        }
    }

    get market(): number{
        return this._product.market;
    }

    set market(value: number){
        if (this.patchValue(this._product, 'market', value)){
            this.ToUpdate = true;
        }
    }

    get weight(): number{
        return this._product.weight;
    }

    set weight(value: number){
        if (this.patchValue(this._product, 'weight', value)){
            this.ToUpdate = true;
        }
    }

    get taxrate(): number{
        // ?? (no ||): null/undefined cae al default del place; un 0 explicito se preserva como Exento.
        return this._product.taxrate ?? this.ticket.place.TaxRate;
    }

    set taxrate(value: number){
        if (this.patchValue(this._product, 'taxrate', value)){
            this.ToUpdate = true;
        }
    }

    get ticket(): Ticket {
        return this._children['ticket'] || null;
    }    

    set ticket(value: Ticket){
        if (this.SetChild('ticket', value, 'ticket')){
            this.ToUpdate = true;
        }
    }

    get comments(): string {
        return this._product.comments;
    }

    set comments(value: string){
        if (this.patchValue(this._product, 'comments', value)){
            this.ToUpdate = true;
        }
    }

    get sort(): number {
        return this._product.sort;
    }

    set sort(value: number){
        if (this.patchValue(this._product, 'sort', value)){
            this.ToUpdate = true;
        }
    }

    get events(): { [key: string]: boolean; } {
        let _flags = this.flags;        

        return {
            AC: _flags[this.TicketProductStatus.IsAC],
            UN: _flags[this.TicketProductStatus.IsUN],
            RD: _flags[this.TicketProductStatus.IsRD],
            PP: _flags[this.TicketProductStatus.IsPP],
            PD: _flags[this.TicketProductStatus.IsPD],
            CC: _flags[this.TicketProductStatus.IsCC],
            DE: _flags[this.TicketProductStatus.IsDE],
        };
    }

    /****************************/
    /* CHILDREN MANAGEMENT      */
    /****************************/

    AddOption(child: TicketOption){
        this.AddChild('options', child, 'product');
    }

    DelOption(child: TicketOption){
        this.DelChild('options', child, 'product');
    }

    /****************************/
    /* CHILD ACCESS             */
    /****************************/

    get options() : Array <TicketOption> {
        return this._chldlist['options'] || [];
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            status: this._product.status,
            product: this._product.product,
            waiter: this._product.waiter,
            offer: this._product.offer,
            price: this._product.price,
            charge: this._product.charge,
            fixed: this._product.fixed ? '1':'0',
            market: this._product.market,
            weight: this._product.weight,
            taxrate: this._product.taxrate,
            ticket: this._product.ticket,
            flags: this._product.flags,
            comments: this._product.comments,
            sort: this._product.sort
        };
    }

    protected get Depend() {
        return {
            ticket: { item: this.ticket, relation_info: { to: 'products', by: 'ticket' } },     // this[by -> 'ticket'][to -> 'products'] => this
            offer: { item: this.offer, relation_info: { to: null, by: 'offer' } },              // no relation to this in this[by -> 'offer']
            product: { item: this.product, relation_info: { to: null, by: 'product' } }         // no relation to this in this[by -> 'product']
            // waiter NO va en Depend: se envía como campo (this._product.waiter) para
            // no notificar/propagar la fila USER del camarero (fuga de PII).
        };
    }

    protected get Children(){
        let _children = [];

        if (this.product){
            _children.push(this.product);
        }

        for(let _item of this.options){
            _children.push(_item)
        }

        if (this.offer){
            _children.push(this.offer);
        }

        return _children;
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    private _patchData(_product: _TicketProduct){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._product, 'status', _product['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'product', _product['product']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'waiter', _product['waiter']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'offer', _product['offer']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'price', _product['price']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'charge', _product['charge']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'taxrate', _product['taxrate']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'fixed', _product['fixed']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'market', _product['market']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'weight', _product['weight']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'ticket', _product['ticket']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'flags', _product['flags']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'comments', _product['comments']) || _toUpdate;
        _toUpdate = this.patchValue(this._product, 'sort', _product['sort']) || _toUpdate;

        return _toUpdate;
    }

    set Data(_product: _TicketProduct){
        this.patchValue(this._product, 'created', _product['created']);
        this.patchValue(this._product, 'updated', _product['updated']);  
        
        if (this._patchData(_product)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._product;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    private DoPatchValues(_product: _TicketProduct){
        this._patchData(_product);

        if (_product['product']){   // update children: 'product'
            let _objid = _product['product'].toString();
            this.SetChild('product', new Product(_objid, this.data, this._objoptions), 'product')
        }
        else {
            this.SetChild('product', null, 'product');
        }

        if (_product['waiter']){    // update children: 'waiter' (USER)
            let _objid = _product['waiter'].toString();
            this.SetChild('waiter', new User(_objid, this.data, this._objoptions), 'waiter')
        }
        else {
            this.SetChild('waiter', null, 'waiter');
        }

        if (_product['ticket']){    // update children: 'ticket'
            let _objid = _product['ticket'].toString();
            this.SetChild('ticket', new Ticket(_objid, this.data, this._objoptions), 'ticket')
        }
        else {
            this.SetChild('ticket', null, 'ticket');
        }

        if (_product['offer']){   // update children: 'offer'
            let _objid = _product['offer'].toString();
            this.SetChild('offer', new TicketOffer(_objid, this.data, this._objoptions), 'offer')
        }
        else {
            this.SetChild('offer', null, 'offer');
        }
    }

    private _ddbb(info): _TicketProductData {
        let _product: _TicketProductData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            status: info['status'],
            product: info['product'] ? parseInt(info['product']) : null,
            waiter: info['waiter'] ? parseInt(info['waiter']) : null,
            offer: info['offer'] ? parseInt(info['offer']) : null,
            price: info['price'] ? parseFloat(info['price']) : null,
            charge: info['charge'] ? parseFloat(info['charge']) : null,
            fixed: (info['fixed'] == '1'),
            market: info['market'] ? parseFloat(info['market']) : null,
            weight: info['weight'] ? parseFloat(info['weight']) : null,
            taxrate: info['taxrate'] ? parseFloat(info['taxrate']) : null,
            ticket: info['ticket'] ? parseInt(info['ticket']) : null,
            flags: info['flags'],
            comments: info['comments'],
            sort: info['sort'] ? parseInt(info['sort']) : null
        };
        return _product;
    }

    protected _OnUpdate(info){
        let _product = this._ddbb(info);
        this.patchValue(this._product, 'objid', _product['objid']);
        this.patchValue(this._product, 'created', _product['created']);
        this.patchValue(this._product, 'updated', _product['updated']);        
        this.DoPatchValues(_product);
        
        if (info['options']){   // update children: 'options'
            this.SetChildren <TicketOption> (info['options'], 'options', TicketOption, 'product');
        }
    }
}

export class TicketProduct extends TicketProductData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('TICKETPRODUCT', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): TicketProduct {
        return this._Copy(store) as TicketProduct;
    }

    /****************************/
    /* DISPLAY HELPERS          */
    /****************************/

    /**
     * Direct variant activa via IsIncluded (tolerante a options
     * extra) y las options que el usuario anadio encima. Null si
     * ningun direct casa. Mismo criterio que ui-change._GetSimilar.
     * Memoized por identidad de options/product/selects — los
     * arrays se reasignan en cada AddChild/DelChild, la cache se
     * invalida sola sin necesidad de eventos.
     */
    private _adCache: { direct: ProductSelect, extras: TicketOption[] } | null = null;
    private _adCacheOptions: TicketOption[] | null = null;
    private _adCacheProduct: Product | null = null;
    private _adCacheSelects: any = null;
    private _adCacheSelectOptions: any[] = [];
    // Snapshot de .option de cada TicketOption — invalida cache cuando
    // una option se resuelve async (null → productOptionRef) sin
    // cambiar el array padre.
    private _adCacheOptionRefs: any[] = [];

    get ActiveDirectInfo() : { direct: ProductSelect, extras: TicketOption[] } | null {
        let _options = this.options;
        let _product = this.product;
        // Cache key sobre _chldlist['selects'] y por cada select su
        // _chldlist['options']. product.selects/directs devuelven
        // copia ordenada nueva cada llamada → no serviria.
        let _selectsRef = _product ? _product._chldlist['selects'] : null;
        let _selectOptionsRefs: any[] = [];
        if (_selectsRef){
            for (let _s of _selectsRef){
                _selectOptionsRefs.push(_s ? _s._chldlist['options'] : null);
            }
        }

        let _selectOptionsHit = (this._adCacheSelectOptions.length === _selectOptionsRefs.length);
        if (_selectOptionsHit){
            for (let _i = 0; _i < _selectOptionsRefs.length; _i++){
                if (this._adCacheSelectOptions[_i] !== _selectOptionsRefs[_i]){
                    _selectOptionsHit = false;
                    break;
                }
            }
        }

        // Snapshot de la ProductOption resuelta en cada TicketOption (puede
        // ser null si aun no se ha hidratado). Comparar por ref detecta el
        // caso offline/async donde el array de TicketOption es el mismo pero
        // cada uno paso de .option=null a .option=resuelto.
        let _optionRefs: any[] = [];
        for (let _opt of _options){
            _optionRefs.push(_opt ? _opt.option : null);
        }
        let _optionRefsHit = (this._adCacheOptionRefs.length === _optionRefs.length);
        if (_optionRefsHit){
            for (let _i = 0; _i < _optionRefs.length; _i++){
                if (this._adCacheOptionRefs[_i] !== _optionRefs[_i]){
                    _optionRefsHit = false;
                    break;
                }
            }
        }

        if ((this._adCacheOptions === _options)
            && (this._adCacheProduct === _product)
            && (this._adCacheSelects === _selectsRef)
            && _selectOptionsHit
            && _optionRefsHit){
            return this._adCache;
        }

        let _result: { direct: ProductSelect, extras: TicketOption[] } | null = null;
        if (_product){
            let _best: ProductSelect | null = null;
            let _bestExtras: TicketOption[] | null = null;
            for (let _direct of _product.directs){
                // Skip directs sin options validas: IsIncluded es
                // vacuous truth (`[].every() = true`) y con
                // first-match-wins usurparian la variante real. Cubre
                // directs con FK huerfana (PRESELECTOPT AC → PRODUCTOPT
                // DE) — reportado "dividi Estrella Galicia salio
                // BALTIC PORTER".
                if (_direct.options.filter(o => o.IsValid).length == 0){
                    continue;
                }
                if (!_direct.IsIncluded(this)){
                    continue;
                }

                // Options del ticketproduct no cubiertas por el direct —
                // la cocina las lista como modificadores. Guard contra
                // TicketOption sin .option resuelta (async) — HasSelection
                // reventaria en null; sin resolver queda como extra.
                let _extras: TicketOption[] = [];
                for (let _option of _options){
                    let _po = _option.option;
                    if (!_po || !_direct.HasSelection(_po)){
                        _extras.push(_option);
                    }
                }

                // First-match-wins en empates — alineado con
                // ui-change._GetSimilar. Si aqui prefiriesemos "mas
                // especifico" carrito y cocina divergerian.
                let _better = (_bestExtras == null)
                    || (_extras.length < _bestExtras.length);
                if (_better){
                    _best = _direct;
                    _bestExtras = _extras;
                }
            }
            if (_best && _bestExtras){
                _result = { direct: _best, extras: _bestExtras };
            }
        }

        this._adCache = _result;
        this._adCacheOptions = _options;
        this._adCacheProduct = _product;
        this._adCacheSelects = _selectsRef;
        this._adCacheSelectOptions = _selectOptionsRefs;
        this._adCacheOptionRefs = _optionRefs;
        return _result;
    }

    /** Just the active direct, or null. */
    get ActiveDirect() : ProductSelect {
        let _info = this.ActiveDirectInfo;
        return _info ? _info.direct : null;
    }

    /** Options the user added on top of the active direct — what cart
     *  views should render as modifiers below the variant header.
     *  Empty array when there is no active direct. */
    get ExtraOptions() : TicketOption[] {
        let _info = this.ActiveDirectInfo;
        return _info ? _info.extras : [];
    }

    /** Flattened ProductOption[] para las vistas del carrito.
     *  Memoize por identidad de ExtraOptions — sin cache,
     *  `.map().filter()` regeneraba array cada CD tick y el *ngFor
     *  OnPush destruia/recreaba las filas por identidad inestable. */
    private _epoCache: ProductOption[] | null = null;
    private _epoCacheSource: TicketOption[] | null = null;
    get ExtraProductOptions() : ProductOption[] {
        let _source = this.ExtraOptions;
        if (this._epoCacheSource === _source && this._epoCache != null){
            return this._epoCache;
        }
        let _result = _source.map(_to => _to.option).filter(o => !!o);
        this._epoCache = _result;
        this._epoCacheSource = _source;
        return _result;
    }

    /** Display name for the ticket product header — the direct's name
     *  when one is active, otherwise the underlying product name. */
    get DisplayName() : string {
        let _direct = this.ActiveDirect;
        return _direct ? _direct.name : (this.product ? this.product.name : '');
    }

    /** Image to use alongside DisplayName: the active direct's avatar,
     *  falling back to the product avatar. */
    get DisplayImg() : string {
        let _direct = this.ActiveDirect;
        if (_direct && _direct.avatar){
            return _direct.avatar;
        }
        return this.product ? this.product.avatar : null;
    }

    /****************************/
    /* BEHAVIOUR UPDATE         */
    /****************************/

    UpRefresh(){    // do not refresh products or offers when updated
        for (let _child in this._children){
            if ([ 'product', 'offer' ].indexOf(_child) != -1){
                continue;
            }

            let _parent = this._children[_child];
            if (_parent && _parent instanceof DataObject){
                _parent.DoRefresh('TICKETPRODUCT');
            }
        }
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string {
        return super.status;
    }

    set status(value: string){
        if (this.status == 'DE' || (this.status == value)){
            return;     // cannot modify deleted items
        }

        if (this.status == 'SP' && (value != 'DE')){
            return;     // sepator items can only be deleted
        }

        // if status is 'PR' or 'AC' delete paid information
        if ([ 'PR', 'AC' ].includes(value)){
            this.DelEvent('PP');
            this.DelEvent('PD');
        }

        // avoid repeating events (ignore PR events)
        if (!['PR', 'SP'].includes(value)){
            this.AddEvent(value);
        }

        // opened ticket cancelation (keep 'CC' and 'DE' flags)
        if (value == 'CC'){
            this.status = (this.ticket.IsPaid) && (!this.ticket.IsToPay) ? 'UN' : 'DE';
        }

        // update status (if suitable)
        if (['AC', 'UN', 'DE', 'SP'].includes(value)){
            super.status = value;
        }
       
        this.ticket.DoRefresh('TICKETPRODUCT');
    }

    get weight(): number{
        return super.weight;
    }

    set weight(value: number){
        if (this.product.IsWeighted){
            super.weight = value;     // cannot set weight if its not weighted
        }
    }

    get group(): number {
        return this._product.group;
    }

    set group(value: number){
        this._product.group = value;
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid() {
        if (!this.status){
            return false;
        }
        
        return (this.status != 'DE') || (this.HasEvent('PD') && this.HasEvent('UN'));
    }

    get Separator(){
        return this.status == 'SP' || ((this.status == 'DE') && (this.product == null));
    }

    get ToRefund() {
        return (this.status == 'UN');
    }

    private _flagIndex(status){
        switch(status){
            case 'AC': return this.TicketProductStatus.IsAC;
            case 'UN': return this.TicketProductStatus.IsUN;
            case 'RD': return this.TicketProductStatus.IsRD;
            case 'PP': return this.TicketProductStatus.IsPP;
            case 'PD': return this.TicketProductStatus.IsPD;
            case 'CC': return this.TicketProductStatus.IsCC;
            case 'DE': return this.TicketProductStatus.IsDE;
        }
        
        return -1;
    }

    HasEvent(event){
        let _flag = this._flagIndex(event);
        if (_flag != -1){
            return (this.flags[_flag]);
        }

        return false;   // flag not found
    }

    AddEvent(event){
        let _flag = this._flagIndex(event);
        if (_flag != -1){
            let _flags = this.flags;
            _flags[_flag] = true;
            this.flags = _flags;
        }
    }

    DelEvent(event){
        let _flag = this._flagIndex(event);
        if (_flag != -1){
            let _flags = this.flags;
            _flags[_flag] = false;
            this.flags = _flags;
        }
    }

    get TotalPrice(){
        if (!this.product){
            return 0;
        }

        let _price: number = this.product.price ? this.product.price : 0;;
        
        // check to use the market price instead of the product price
        if (this.product.MarketPrice){
            _price = this.market;   
        }

        // calculate the price based on the weight and the current price per kg
        if (this.product.IsWeighted){
            _price = Math.round(_price * (this.weight/1000) * 100) / 100;
        }

        for(let _option of this.options){
            _price += _option.option ? _option.option.price : 0;
        }
        
        return _price;
    }

    _OfferPrice(offer){
        let _price: number = 0;
        for(let _option of this.options){
            if (!offer.IncludesOption(_option.option)){
                _price += _option.option ? Math.max(_option.option.price, 0) : 0;
            }
        }
        return _price;
    }

    get OfferPrice(){
        let _offer = (this.offer && this.offer.IsValid) ? this.offer.offer : null;
        if (_offer){
            return this._OfferPrice(_offer)
        }

        return 0;   // not in offer
    }

    get FixedPrice(){
        if (this.fixed){
            return this.charge;
        }
        return 0;
    }

    OfferCharge(offerprice: number){
        let _charge: number = this.TotalPrice;
        if (this.fixed){
            _charge = this.charge;
        }

        if (this.offer && this.offer.IsValid){
            let _ofprice = offerprice || this.offer.fixed ? this.offer.price : this.offer.offer.price;

            // Denominador de la distribucion proporcional de la oferta:
            // deduplicado (UniqueProducts) para que una linea fantasma transitoria
            // (copia optimista + fila persistida) no infle _ttprice y descuadre el
            // reparto. Coherente con los sumatorios exteriores (TicketOffer), que
            // ya usan UniqueProducts.
            let _ttprice = 0;   // price for all products in this offer
            for (let _ticketproduct of this.ticket.UniqueProducts){
                if (_ticketproduct.IsValid){
                    if ((_ticketproduct.offer == this.offer)){
                        _ttprice += _ticketproduct.TotalPrice;
                    }    
                }
            }  

            if (_ttprice == 0){
                return 0;
            }

            // proportional price for the product for the applied offer price
            _charge = this.OfferPrice + ((this.TotalPrice * _ofprice) / _ttprice);
        }
        
        return _charge;
    }

    get ChargePrice(){
        if (this.status == 'DE'){
            return 0;   // removed product (do not charge)
        }

        let _c = this.OfferCharge(null);
        return _c;
    }

    get RefundPrice(){
        if ((this.status != 'DE') || (!this.HasEvent('PD'))){
            return 0;   // not removed or not paid product (not a refund)
        }

        return this.OfferCharge(null);
    }

    CartAdd(option: ProductOption){
        let _ticketoption: TicketOption = new TicketOption(null, this.data);
        if (_ticketoption){
            _ticketoption.product = this;
            _ticketoption.option = option;
            
            this.AddOption(_ticketoption);
        }
    }
}


